/**
 * PlayerAvatar molecule — the player card's avatar (SE-5492).
 *
 * @remarks
 * Renders the canonical player image when one exists and the initials circle
 * when it does not. It COMPOSES the existing `InitialsAvatar` molecule for the
 * fallback branch rather than re-implementing the circle, so the Player Card v7
 * avatar treatment stays defined in exactly one place.
 *
 * **No photo renders today, and that is correct.** `Player.imageUrl` (SE-5538)
 * is schema + API plumbing only — no image source is populated yet, so the
 * field is null for every player and this component always takes the initials
 * branch. Wiring the real field now means population lands with no client
 * change. A blank photo is not a bug; an invented placeholder URL would be.
 * @module components/molecules/PlayerAvatar/PlayerAvatarView
 */
import React, { memo } from "react";

import { Avatar, AvatarImage } from "@/components/atoms/Avatar";
import InitialsAvatar from "@/components/molecules/InitialsAvatar";
import { derivePlayerInitials } from "@/utils/playerInitials";

/** Props for PlayerAvatarView */
interface PlayerAvatarViewProps {
  /** Player display name — the initials fallback is derived from this. */
  readonly playerName: string;
  /**
   * Canonical player image URL. Null/absent for every player today (SE-5538),
   * which is the supported and expected state.
   */
  readonly imageUrl?: string | null;
  /** testID forwarded to whichever branch renders. */
  readonly testID?: string;
  /** 24px (`sm`, the default) or 44px (`lg`) — see InitialsAvatar. */
  readonly size?: "sm" | "lg";
}

/**
 * Render the player's avatar: photo when available, initials otherwise.
 * @param root0 - Component props
 * @param root0.playerName - Player display name
 * @param root0.imageUrl - Canonical image URL, or null
 * @param root0.testID - testID forwarded to the rendered branch
 * @param root0.size - 24px (`sm`) or 44px (`lg`)
 * @returns The avatar element.
 */
const PlayerAvatarView = ({
  playerName,
  imageUrl,
  testID,
  size = "sm",
}: PlayerAvatarViewProps) =>
  // Guard on a non-blank string, not merely non-null: a whitespace-only URL
  // would render a permanently broken image rather than the fallback.
  imageUrl && imageUrl.trim().length > 0 ? (
    <Avatar size={size === "lg" ? "md" : "xs"} testID={testID}>
      <AvatarImage
        source={{ uri: imageUrl }}
        alt={`${playerName} photo`}
        accessibilityLabel={`${playerName} photo`}
      />
    </Avatar>
  ) : (
    <InitialsAvatar
      initials={derivePlayerInitials(playerName)}
      testID={testID}
      size={size}
    />
  );

PlayerAvatarView.displayName = "PlayerAvatarView";

export default memo(PlayerAvatarView);
